/**
 * 自动草稿：把最近一次文档状态存进 IndexedDB，防误关。
 *
 * 三个设计点：
 *  1) **节流**：文档每次改动都写盘会拖慢编辑；默认最多每 3s 落一次，另外提供 flush() 给 pagehide 用。
 *  2) **双保险**：写入时把旧的 latest 轮转成 previous（PLAN 只要"最近一次"，多留一格几乎零成本）。
 *  3) **存储抽象**：IndexedDB 在隐私模式/测试环境可能不可用 → 用 `DraftStorage` 接口隔离，
 *     生产给 IDB 实现，测试给内存实现，桥接层永远只依赖接口。
 */
export type DraftKey = 'latest' | 'previous';

export interface DraftRecord {
  key: DraftKey;
  /** 写入时间（ms） */
  savedAt: number;
  /** 文档名（若来自文件） */
  name: string | null;
  /** 整篇 SVG 源 */
  svg: string;
  /** 视图态与 UI 态（可选，用于恢复现场） */
  view?: { zoom: number; panX: number; panY: number };
}

export interface DraftStorage {
  read(key: DraftKey): Promise<DraftRecord | null>;
  write(record: DraftRecord): Promise<void>;
  clear(): Promise<void>;
}

/** 内存实现（测试与降级用） */
export function createMemoryStorage(seed: Record<string, DraftRecord> = {}): DraftStorage {
  const map = new Map<string, DraftRecord>(Object.entries(seed));
  return {
    async read(key) {
      return map.get(key) ?? null;
    },
    async write(record) {
      map.set(record.key, record);
    },
    async clear() {
      map.clear();
    },
  };
}

const STORE = 'drafts';

/** IndexedDB 实现；环境不支持时返回 null（调用方降级） */
export function createIdbStorage(dbName = 'svg-edit-lite'): DraftStorage | null {
  if (typeof indexedDB === 'undefined') return null;

  const open = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB 打开失败'));
    });

  const tx = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB 操作失败'));
      transaction.oncomplete = () => db.close();
    });
  };

  return {
    read: (key) => tx<DraftRecord | undefined>('readonly', (s) => s.get(key)).then((r) => r ?? null),
    write: (record) => tx<void>('readwrite', (s) => s.put(record)),
    clear: () => tx<void>('readwrite', (s) => s.clear()),
  };
}

export interface DraftStore {
  /** 节流保存：多次调用只落一次盘；snapshot 在真正落盘时才求值 */
  schedule(snapshot: () => Omit<DraftRecord, 'key' | 'savedAt'> | null): void;
  /** 立刻落盘（pagehide / 手动） */
  flush(): Promise<void>;
  load(): Promise<DraftRecord | null>;
  loadPrevious(): Promise<DraftRecord | null>;
  clear(): Promise<void>;
  /** 本会话是否已排入未落盘的改动（状态栏提示用） */
  pending(): boolean;
}

export interface DraftStoreOptions {
  /** 节流窗口（ms），默认 3000 */
  throttleMs?: number;
  now?: () => number;
}

export function createDraftStore(storage: DraftStorage, opts: DraftStoreOptions = {}): DraftStore {
  const throttleMs = opts.throttleMs ?? 3000;
  const now = opts.now ?? (() => Date.now());

  let timer: ReturnType<typeof setTimeout> | null = null;
  let nextAllowed = 0;
  let pendingFlush: (() => Omit<DraftRecord, 'key' | 'savedAt'> | null) | null = null;

  const writeLatest = async (payload: Omit<DraftRecord, 'key' | 'savedAt'>): Promise<void> => {
    const previous = await storage.read('latest');
    if (previous) await storage.write({ ...previous, key: 'previous' });
    await storage.write({ ...payload, key: 'latest', savedAt: now() });
  };

  const store: DraftStore = {
    schedule(snapshot) {
      pendingFlush = snapshot;
      if (timer !== null) return;
      const wait = Math.max(0, nextAllowed - now());
      timer = setTimeout(() => {
        timer = null;
        nextAllowed = now() + throttleMs;
        void store.flush(); // 用 store 而不是 this：箭头函数里的 this 容易误读
      }, wait);
    },

    async flush() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      const snapshot = pendingFlush;
      pendingFlush = null;
      if (!snapshot) return;
      const payload = snapshot();
      if (!payload) return;
      await writeLatest(payload);
    },

    load: () => storage.read('latest'),
    loadPrevious: () => storage.read('previous'),
    clear: async () => {
      pendingFlush = null;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      await storage.clear();
    },
    pending: () => pendingFlush !== null || timer !== null,
  };
  return store;
}
